import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import { attachDisplayUrl } from '../shared/heicDisplay';
import {
    IconDownload,
    IconPhoto,
    IconRefresh,
    IconTextSize,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import ImageGridPreview from '../shared/ImageGridPreview';
import useImageFileEdits from '../../components/image/useImageFileEdits';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
import {
    POSITION_OPTIONS,
    formatFileSize,
    isSupportedWatermarkImageFile,
    watermarkImageFile,
} from './watermarkImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_WATERMARK_IMAGE_BYTES = 2 * 1024 * 1024;

const ACCEPT = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/heic',
    'image/heif',
    'image/avif',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.heic',
    '.heif',
    '.avif',
].join(',');

const DEFAULT_SETTINGS = {
    mode: 'text',
    text: '© Your Watermark',
    textColor: '#ffffff',
    position: 'center',
    opacity: 50,
    fontSize: 96,
    imageScale: 20,
    rotation: 0,
    outlineEnabled: false,
    outlineColor: '#000000',
    outlineWidth: 2,
    repeat: false,
};

const POSITION_LABEL_KEYS = {
    center: 'watermarkImage.positions.center',
    'top-left': 'watermarkImage.positions.topLeft',
    'top-right': 'watermarkImage.positions.topRight',
    'bottom-left': 'watermarkImage.positions.bottomLeft',
    'bottom-right': 'watermarkImage.positions.bottomRight',
};

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/**
 * Position glyph: a page frame with the mark sitting where it will actually
 * land, so the corner is readable without parsing the label.
 */
function PositionGlyph({ position, active }) {
    const color = active ? '#F59E0B' : '#9A9AA3';
    const spot = {
        center: [12, 12],
        'top-left': [7.5, 7.5],
        'top-right': [16.5, 7.5],
        'bottom-left': [7.5, 16.5],
        'bottom-right': [16.5, 16.5],
    }[position] ?? [12, 12];
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2.5" fill="none" stroke={color} strokeWidth="1.6" />
            <rect x={spot[0] - 3} y={spot[1] - 2} width="6" height="4" rx="1" fill={color} />
        </svg>
    );
}

const inputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-2.5 text-sm text-[#3D3833] outline-none placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

/**
 * Cards for every picked image, with the live watermark preview underneath.
 *
 * The grid matches the other image tools (and carries the per-image actions);
 * the panel below keeps the thing this tool exists for — seeing the watermark
 * land on the picture while the settings are being changed — for whichever card
 * is selected.
 */
function WatermarkImagePreview({
    files,
    results,
    livePreviewUrl,
    activeIndex,
    onActiveIndexChange,
    actions = [],
    busyIndexes,
    onAddMore,
    onAddDrop,
    addLabel,
}) {
    const { t } = useTranslation();
    const activeFile = files[activeIndex] ?? files[0] ?? null;
    const activeResult = results[activeIndex] ?? null;

    const [originalSrc, setOriginalSrc] = useState(null);
    useEffect(() => {
        if (!activeFile) {
            setOriginalSrc(null);
            return undefined;
        }
        // HEIC uploads are decoded first — see shared/heicDisplay.
        return attachDisplayUrl(activeFile, setOriginalSrc);
    }, [activeFile]);

    const [resultSrc, setResultSrc] = useState(null);
    useEffect(() => {
        if (!activeResult?.blob) {
            setResultSrc(null);
            return undefined;
        }
        const url = URL.createObjectURL(activeResult.blob);
        setResultSrc(url);
        return () => URL.revokeObjectURL(url);
    }, [activeResult]);

    if (!activeFile && !activeResult) return null;

    const src = resultSrc || livePreviewUrl || originalSrc;

    return (
        <div className="flex h-full min-h-0 flex-col gap-3">
            <div className="min-h-0 flex-1">
                <ImageGridPreview
                    files={files}
                    results={results}
                    variant="preview"
                    actions={actions}
                    activeIndex={activeIndex}
                    onItemClick={onActiveIndexChange}
                    busyIndexes={busyIndexes}
                    onAddMore={onAddMore}
                    onAddDrop={onAddDrop}
                    addLabel={addLabel}
                />
            </div>

            {src ? (
                <div className="flex h-[220px] flex-none items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
                    <img
                        src={src}
                        alt={activeResult?.name ?? activeFile?.name ?? t('watermarkImage.previewTitle')}
                        className="max-h-full max-w-full object-contain"
                        draggable={false}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default function WatermarkImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const watermarkImageInputRef = useRef(null);
    const previewCacheRef = useRef({});

    const [files, setFiles] = useState([]);
    const [mode, setMode] = useState(DEFAULT_SETTINGS.mode);
    const [text, setText] = useState(DEFAULT_SETTINGS.text);
    const [format, setFormat] = useState('original');
    const [textColor, setTextColor] = useState(DEFAULT_SETTINGS.textColor);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [position, setPosition] = useState(DEFAULT_SETTINGS.position);
    const [opacity, setOpacity] = useState(DEFAULT_SETTINGS.opacity);
    const [fontSize, setFontSize] = useState(DEFAULT_SETTINGS.fontSize);
    const [imageScale, setImageScale] = useState(DEFAULT_SETTINGS.imageScale);
    const [rotation, setRotation] = useState(DEFAULT_SETTINGS.rotation);
    const [outlineEnabled, setOutlineEnabled] = useState(DEFAULT_SETTINGS.outlineEnabled);
    const [outlineColor, setOutlineColor] = useState(DEFAULT_SETTINGS.outlineColor);
    const [outlineWidth, setOutlineWidth] = useState(DEFAULT_SETTINGS.outlineWidth);
    const [repeat, setRepeat] = useState(DEFAULT_SETTINGS.repeat);
    const [isDragging, setIsDragging] = useState(false);
    const [logoDragging, setLogoDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [livePreviewUrl, setLivePreviewUrl] = useState(null);

    const invalidateResults = useCallback(() => {
        setResults([]);
        setError(null);
        setProgress(null);
        previewCacheRef.current = {};
    }, []);

    const watermarkOptions = useMemo(() => ({
        mode,
        text,
        textColor,
        imageFile,
        position,
        opacity,
        fontSize,
        imageScale,
        rotation,
        outlineEnabled,
        outlineColor,
        outlineWidth,
        repeat,
    }), [mode, text, textColor, imageFile, position, opacity, fontSize, imageScale, rotation, outlineEnabled, outlineColor, outlineWidth, repeat]);

    const settingsKey = useMemo(() => JSON.stringify({
        mode,
        text,
        textColor,
        position,
        opacity,
        fontSize,
        imageScale,
        rotation,
        outlineEnabled,
        outlineColor,
        outlineWidth,
        repeat,
        logo: imageFile ? `${imageFile.name}-${imageFile.size}` : '',
    }), [mode, text, textColor, position, opacity, fontSize, imageScale, rotation, outlineEnabled, outlineColor, outlineWidth, repeat, imageFile]);

    // Live preview (debounced), matching site behavior
    useEffect(() => {
        const selectedFile = files[previewIndex] ?? null;
        const noContent = mode === 'text' ? !text.trim() : !imageFile;

        if (!selectedFile || noContent || results.length > 0) {
            setLivePreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            return undefined;
        }

        // The cache is per position, so it also has to know WHICH file sat
        // there: cropping or rotating an image keeps its slot and its settings,
        // and the old preview would otherwise stay on screen.
        const fileKey = `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`;
        const cached = previewCacheRef.current[previewIndex];
        if (cached && cached.settingsKey === settingsKey && cached.fileKey === fileKey) {
            const url = URL.createObjectURL(cached.blob);
            setLivePreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return url;
            });
            return undefined;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const result = await watermarkImageFile(selectedFile, watermarkOptions);
                if (cancelled) return;
                previewCacheRef.current[previewIndex] = { settingsKey, fileKey, blob: result.blob };
                const url = URL.createObjectURL(result.blob);
                setLivePreviewUrl((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return url;
                });
            } catch {
                // Keep the previous preview visible on failure.
            }
        }, 450);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [files, previewIndex, settingsKey, watermarkOptions, mode, text, imageFile, results.length]);

    useEffect(() => () => {
        if (livePreviewUrl) URL.revokeObjectURL(livePreviewUrl);
    }, [livePreviewUrl]);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedWatermarkImageFile);
        if (!images.length) {
            const msg = t('watermarkImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('watermarkImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    const loadWatermarkImage = useCallback((incoming) => {
        const file = Array.from(incoming || [])[0];
        if (!file) return;
        if (!(file.type || '').startsWith('image/')) {
            const msg = t('watermarkImage.errors.invalidWatermarkImage');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (file.size > MAX_WATERMARK_IMAGE_BYTES) {
            const msg = t('watermarkImage.errors.watermarkImageTooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        invalidateResults();
    }, [imagePreview, invalidateResults, t]);

    const clearWatermarkImage = () => {
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImageFile(null);
        setImagePreview(null);
        invalidateResults();
    };

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex((prev) => Math.max(0, Math.min(prev, files.length - 2)));
        invalidateResults();
    };

    // Crop / rotate / duplicate / delete, shared with every other image tool.
    const { actions: fileActions, cropDialog } = useImageFileEdits({
        files,
        onReplace: (index, file) => {
            setFiles((prev) => prev.map((item, i) => (i === index ? file : item)));
            invalidateResults();
        },
        onInsert: (index, file) => {
            setFiles((prev) => {
                const next = [...prev];
                next.splice(index + 1, 0, file);
                return next;
            });
            invalidateResults();
        },
        onRemove: removeFile,
    });

    const handleReset = () => {
        setFiles([]);
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
        setMode(DEFAULT_SETTINGS.mode);
        setText(DEFAULT_SETTINGS.text);
        setTextColor(DEFAULT_SETTINGS.textColor);
        clearWatermarkImage();
        setPosition(DEFAULT_SETTINGS.position);
        setOpacity(DEFAULT_SETTINGS.opacity);
        setFontSize(DEFAULT_SETTINGS.fontSize);
        setImageScale(DEFAULT_SETTINGS.imageScale);
        setRotation(DEFAULT_SETTINGS.rotation);
        setOutlineEnabled(DEFAULT_SETTINGS.outlineEnabled);
        setOutlineColor(DEFAULT_SETTINGS.outlineColor);
        setOutlineWidth(DEFAULT_SETTINGS.outlineWidth);
        setRepeat(DEFAULT_SETTINGS.repeat);
        previewCacheRef.current = {};
        setLivePreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    };

    const resetStyleSettings = () => {
        setPosition(DEFAULT_SETTINGS.position);
        setOpacity(DEFAULT_SETTINGS.opacity);
        setFontSize(DEFAULT_SETTINGS.fontSize);
        setImageScale(DEFAULT_SETTINGS.imageScale);
        setRotation(DEFAULT_SETTINGS.rotation);
        setOutlineEnabled(DEFAULT_SETTINGS.outlineEnabled);
        setOutlineColor(DEFAULT_SETTINGS.outlineColor);
        setOutlineWidth(DEFAULT_SETTINGS.outlineWidth);
        setRepeat(DEFAULT_SETTINGS.repeat);
        setTextColor(DEFAULT_SETTINGS.textColor);
        invalidateResults();
    };

    // Files with no result yet — adding pictures to a finished batch queues only
    // the new ones, so nothing already stamped is redone.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (results[i] ? acc : [...acc, i]), []),
        [files, results],
    );
    const allDone = files.length > 0 && pendingIndexes.length === 0;
    const canApply = pendingIndexes.length > 0
        && (mode === 'text' ? Boolean(text.trim()) : Boolean(imageFile));

    const handleApply = async () => {
        if (!canApply || processing) return;
        if (mode === 'text' && !text.trim()) {
            const msg = t('watermarkImage.errors.emptyText');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (mode === 'image' && !imageFile) {
            const msg = t('watermarkImage.errors.noImage');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        recordToolUsage('image/watermark', { category: 'image' });

        setProcessing(true);
        setError(null);
        const queue = pendingIndexes;

        try {
            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                setProgress({ fileIndex: k + 1, fileTotal: queue.length });
                const result = await watermarkImageFile(files[i], { ...watermarkOptions, format });

                // Written at the file's own index (results line up with files) and
                // published immediately, so its card stops spinning right away.
                setResults((prev) => {
                    const next = [...prev];
                    next[i] = result;
                    return next;
                });
            }
            notifications.show({ title: t('common.success'), message: t('watermarkImage.done'), color: 'green' });
        } catch (err) {
            let msg = t('watermarkImage.errors.failed');
            if (err?.message === 'emptyText') msg = t('watermarkImage.errors.emptyText');
            else if (err?.message === 'noImage') msg = t('watermarkImage.errors.noImage');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const doneResults = useMemo(() => results.filter(Boolean), [results]);

    const handleDownload = async () => {
        if (!doneResults.length) return;
        if (doneResults.length === 1) {
            downloadBlob(doneResults[0].blob, doneResults[0].name);
            return;
        }
        const zip = await zipImageFiles(doneResults.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'watermarked-images.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: !!canApply,
        processing,
        hasResult: doneResults.length > 0,
        hasError: !!error,
    });

    const successLabel = doneResults.length > 1
        ? `${t('watermarkImage.resultsTitle')} (${toBnDigits(doneResults.length)})`
        : t('watermarkImage.done');

    const processingLabel = progress?.fileTotal > 1
        ? t('watermarkImage.progressFileOnly', {
            file: toBnDigits(progress.fileIndex),
            files: toBnDigits(progress.fileTotal),
        })
        : t('watermarkImage.applying');

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('watermarkImage.done'),
    });

    const patchSetting = (fn) => {
        fn();
        invalidateResults();
    };

    return (
        <>
        <ToolPageLayout
            slug="image/watermark"
            subtitleKey="watermarkImage.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: doneResults.length > 1 ? t('watermarkImage.downloadZip') : t('watermarkImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('watermarkImage.apply'),
                onClick: handleApply,
                disabled: !canApply || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('watermarkImage.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            // The preview column tracks the settings column's height — including
            // the empty state, where the settings are already full length.
            matchSidebarHeight
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />
                    <input
                        ref={watermarkImageInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                        className="hidden"
                        onChange={(e) => { loadWatermarkImage(e.target.files); e.target.value = ''; }}
                    />

                    <ToolCard title={t('watermarkImage.typeTitle')}>
                        <div className="space-y-3">
                            {/* Icons only — the name is in the tooltip and read out
                                via aria-label. */}
                            <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                <Hint label={t('watermarkImage.modeText')}>
                                    <button
                                        type="button"
                                        onClick={() => patchSetting(() => setMode('text'))}
                                        disabled={processing}
                                        className={segBtnClass(mode === 'text')}
                                        aria-label={t('watermarkImage.modeText')}
                                        aria-pressed={mode === 'text'}
                                    >
                                        <IconTextSize size={20} stroke={1.8} />
                                    </button>
                                </Hint>
                                <Hint label={t('watermarkImage.modeImage')}>
                                    <button
                                        type="button"
                                        onClick={() => patchSetting(() => setMode('image'))}
                                        disabled={processing}
                                        className={segBtnClass(mode === 'image')}
                                        aria-label={t('watermarkImage.modeImage')}
                                        aria-pressed={mode === 'image'}
                                    >
                                        <IconPhoto size={20} stroke={1.8} />
                                    </button>
                                </Hint>
                            </div>

                            {mode === 'text' ? (
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <label htmlFor="wm-image-text" className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('watermarkImage.textLabel')}
                                        </label>
                                        <input
                                            id="wm-image-text"
                                            type="text"
                                            value={text}
                                            onChange={(e) => patchSetting(() => setText(e.target.value))}
                                            placeholder={t('watermarkImage.textPlaceholder')}
                                            className={inputClass}
                                        />
                                        <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">{t('watermarkImage.textHint')}</p>
                                    </div>
                                    <label htmlFor="wm-font-size" className="block space-y-1.5">
                                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('watermarkImage.fontSizeLabel', { value: toBnDigits(fontSize) })}
                                        </span>
                                        <input
                                            id="wm-font-size"
                                            type="range"
                                            min={14}
                                            max={300}
                                            value={fontSize}
                                            onChange={(e) => patchSetting(() => setFontSize(Number(e.target.value)))}
                                            className="w-full accent-amber-500"
                                        />
                                    </label>
                                    <label className="flex items-center gap-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                        {t('watermarkImage.textColor')}
                                        <input
                                            type="color"
                                            value={textColor}
                                            onChange={(e) => patchSetting(() => setTextColor(e.target.value))}
                                            className="h-9 w-12 cursor-pointer rounded-lg border border-[#ECE8E4] bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900"
                                        />
                                        <input
                                            type="text"
                                            value={textColor}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setTextColor(v);
                                            }}
                                            onBlur={() => {
                                                if (/^#[0-9a-fA-F]{6}$/.test(textColor)) {
                                                    invalidateResults();
                                                } else {
                                                    setTextColor(DEFAULT_SETTINGS.textColor);
                                                    invalidateResults();
                                                }
                                            }}
                                            className="w-[88px] rounded-lg border border-[#ECE8E4] bg-white px-2 py-1.5 text-xs font-mono text-[#3D3833] outline-none focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                        />
                                    </label>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {!imageFile ? (
                                        // A dashed box that only answers clicks reads
                                        // as broken, so the logo tile takes a drop too.
                                        <button
                                            type="button"
                                            onClick={() => watermarkImageInputRef.current?.click()}
                                            onDragOver={(e) => { e.preventDefault(); setLogoDragging(true); }}
                                            onDragLeave={() => setLogoDragging(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setLogoDragging(false);
                                                loadWatermarkImage(e.dataTransfer.files);
                                            }}
                                            className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 transition-colors ${
                                                logoDragging
                                                    ? 'border-amber-500 bg-amber-50/70 dark:border-amber-500 dark:bg-amber-900/20'
                                                    : 'border-[#ECE8E4] bg-[#F7F5F3]/80 hover:border-amber-400/60 dark:border-zinc-700 dark:bg-zinc-800/50'
                                            }`}
                                        >
                                            <IconPhoto size={24} className="text-amber-600" />
                                            <span className="text-sm font-semibold text-[#3D3833] dark:text-zinc-200">{t('watermarkImage.imageUpload')}</span>
                                            <span className="text-xs text-[#A8A099] dark:text-zinc-500">{t('watermarkImage.imageHint')}</span>
                                        </button>
                                    ) : (
                                        // Dropping onto the filled row swaps the logo,
                                        // so a second file needs no detour through
                                        // "remove" first.
                                        <div
                                            onDragOver={(e) => { e.preventDefault(); setLogoDragging(true); }}
                                            onDragLeave={() => setLogoDragging(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setLogoDragging(false);
                                                loadWatermarkImage(e.dataTransfer.files);
                                            }}
                                            className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
                                                logoDragging
                                                    ? 'border-amber-500 bg-amber-50/60 dark:border-amber-500 dark:bg-amber-900/20'
                                                    : 'border-[#ECE8E4] dark:border-zinc-700'
                                            }`}
                                        >
                                            {imagePreview ? (
                                                <img src={imagePreview} alt="" className="h-14 w-14 rounded-lg border border-[#ECE8E4] object-contain dark:border-zinc-700" />
                                            ) : null}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium text-[#3D3833] dark:text-zinc-100">{imageFile.name}</p>
                                                <p className="text-xs text-[#A8A099] dark:text-zinc-500">{formatFileSize(imageFile.size)}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={clearWatermarkImage}
                                                className="rounded-lg border border-[#ECE8E4] px-2.5 py-1 text-xs font-semibold text-[#6B635C] transition-colors hover:border-red-200 hover:text-red-500 dark:border-zinc-700 dark:text-zinc-300"
                                            >
                                                {t('watermarkImage.removeImage')}
                                            </button>
                                        </div>
                                    )}
                                    <label htmlFor="wm-image-scale" className="block space-y-1.5">
                                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('watermarkImage.scaleLabel', { value: toBnDigits(imageScale) })}
                                        </span>
                                        <input
                                            id="wm-image-scale"
                                            type="range"
                                            min={5}
                                            max={60}
                                            value={imageScale}
                                            onChange={(e) => patchSetting(() => setImageScale(Number(e.target.value)))}
                                            className="w-full accent-amber-500"
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    </ToolCard>

                    <ToolCard
                        title={t('watermarkImage.styleTitle')}
                        headerAction={(
                            <button
                                type="button"
                                onClick={resetStyleSettings}
                                className="text-[12px] font-semibold text-primary transition-opacity"
                            >
                                {t('watermarkImage.resetStyle')}
                            </button>
                        )}
                    >
                        <div className="space-y-3.5">
                            <div>
                                <p className="mb-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('watermarkImage.positionTitle')}
                                </p>
                                {/* Glyphs only — each one already draws where the mark
                                    lands, and the name is in the tooltip / aria-label —
                                    so all five sit on one row. */}
                                <div className="grid grid-cols-5 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                    {POSITION_OPTIONS.map((id) => (
                                        <Hint label={t(POSITION_LABEL_KEYS[id])} key={id}>
                                            <button
                                                type="button"
                                                onClick={() => patchSetting(() => setPosition(id))}
                                                disabled={processing}
                                                className={segBtnClass(position === id)}
                                                aria-label={t(POSITION_LABEL_KEYS[id])}
                                                aria-pressed={position === id}
                                            >
                                                <PositionGlyph position={id} active={position === id} />
                                            </button>
                                        </Hint>
                                    ))}
                                </div>
                            </div>

                            <label htmlFor="wm-image-opacity" className="block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('watermarkImage.opacityLabel', { value: toBnDigits(opacity) })}
                                </span>
                                <input
                                    id="wm-image-opacity"
                                    type="range"
                                    min={5}
                                    max={100}
                                    value={opacity}
                                    onChange={(e) => patchSetting(() => setOpacity(Number(e.target.value)))}
                                    className="w-full accent-amber-500"
                                />
                            </label>

                            <label htmlFor="wm-rotation" className="block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('watermarkImage.rotationLabel', { value: toBnDigits(rotation) })}
                                </span>
                                <input
                                    id="wm-rotation"
                                    type="range"
                                    min={-180}
                                    max={180}
                                    value={rotation}
                                    onChange={(e) => patchSetting(() => setRotation(Number(e.target.value)))}
                                    className="w-full accent-amber-500"
                                />
                            </label>

                            {mode === 'text' ? (
                                <div className="space-y-3">
                                    <label className="flex cursor-pointer items-center justify-between gap-3 text-[13px] font-medium text-[#3D3833] dark:text-zinc-200">
                                        <span>{t('watermarkImage.outline')}</span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={outlineEnabled}
                                            onClick={() => patchSetting(() => setOutlineEnabled(!outlineEnabled))}
                                            className={`relative h-[22px] w-10 flex-none rounded-full transition-colors ${
                                                outlineEnabled ? 'bg-primary' : 'bg-[#D6D6DE] dark:bg-zinc-600'
                                            }`}
                                        >
                                            <span
                                                className={`absolute top-[3px] block h-4 w-4 rounded-full bg-white shadow transition-[left] ${
                                                    outlineEnabled ? 'left-[21px]' : 'left-[3px]'
                                                }`}
                                            />
                                        </button>
                                    </label>
                                    {outlineEnabled ? (
                                        <>
                                            <label className="flex items-center gap-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                                {t('watermarkImage.outlineColor')}
                                                <input
                                                    type="color"
                                                    value={outlineColor}
                                                    onChange={(e) => patchSetting(() => setOutlineColor(e.target.value))}
                                                    className="h-9 w-12 cursor-pointer rounded-lg border border-[#ECE8E4] bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900"
                                                />
                                            </label>
                                            <label htmlFor="wm-outline-width" className="block space-y-1.5">
                                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                                    {t('watermarkImage.outlineWidth', { value: toBnDigits(outlineWidth) })}
                                                </span>
                                                <input
                                                    id="wm-outline-width"
                                                    type="range"
                                                    min={1}
                                                    max={10}
                                                    value={outlineWidth}
                                                    onChange={(e) => patchSetting(() => setOutlineWidth(Number(e.target.value)))}
                                                    className="w-full accent-amber-500"
                                                />
                                            </label>
                                        </>
                                    ) : null}
                                </div>
                            ) : null}

                            <label className="flex cursor-pointer items-start justify-between gap-3 text-[13px] font-medium text-[#3D3833] dark:text-zinc-200">
                                <span>
                                    <span className="block">{t('watermarkImage.repeat')}</span>
                                    <span className="mt-0.5 block text-[11px] font-normal text-[#A8A099] dark:text-zinc-500">
                                        {t('watermarkImage.repeatHint')}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={repeat}
                                    onClick={() => patchSetting(() => setRepeat(!repeat))}
                                    className={`relative mt-0.5 h-[22px] w-10 flex-none rounded-full transition-colors ${
                                        repeat ? 'bg-primary' : 'bg-[#D6D6DE] dark:bg-zinc-600'
                                    }`}
                                >
                                    <span
                                        className={`absolute top-[3px] block h-4 w-4 rounded-full bg-white shadow transition-[left] ${
                                            repeat ? 'left-[21px]' : 'left-[3px]'
                                        }`}
                                    />
                                </button>
                            </label>
                        </div>
                    </ToolCard>

                    <OutputFormatPicker
                        value={format}
                        onChange={(next) => { setFormat(next); invalidateResults(); }}
                        disabled={processing}
                    />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('watermarkImage.previewTitle')}
                    fill
                    tall={false}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('watermarkImage.previewEmpty')}
                    idleHint={t('watermarkImage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress?.fileTotal
                        ? Math.round((progress.fileIndex / progress.fileTotal) * 100)
                        : undefined}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showPasteHint
                    showIdle={!files.length && !error}
                >
                    {/* Kept mounted while processing: each card spins on its own, so
                        the live preview below stays readable through the run. */}
                    {files.length > 0 && !error ? (
                        <WatermarkImagePreview
                            actions={processing || doneResults.length ? [] : fileActions}
                            files={files}
                            results={results}
                            livePreviewUrl={livePreviewUrl}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                            busyIndexes={processing ? pendingIndexes : undefined}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('watermarkImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        {cropDialog}
        </>
    );
}
